import React, { useState, Fragment } from 'react';

import { ReactComponent as Chevron } from 'icons/icon-chevron.svg';
import { DSPCustomers } from '../shared/customerProptypes';
import CustomerIcon from '../../../base/CustomerIcon/CustomerIcon';

import pluralize from 'helpers/pluralize';

import {
  RootLayout,
  LayoutWrapper,
  Label,
  Radio,
  ShowMore,
} from './DSPToggle.style';

import { Media, MediaFigure, MediaBody } from 'components/base/Media/Media';
import styled from 'styled-components/macro';
import generateRandomHash from 'helpers/generateRandomHash';

const DSPToggleMediaFigure = styled(MediaFigure)`
  height: 2rem;
`;

const DspToggleMediaBody = styled(MediaBody)`
  align-self: center;
`;

export interface IToggleOption {
  label: string;
  value: string;
  positionsCount: number;
}

interface IDSPToggleProps {
  selectedCustomer: IToggleOption;
  setSelectedCustomer: (customer: any) => void;
  options: IToggleOption[];
  variation: string;
  disabled?: boolean;
  limit?: number;
  isIconDisplayed: boolean;
}

/**
 * The DSP toggle for desktop screensizes creates a radio input
 * for every customer. When there are more customers than 5,
 * a button appears which can be clicked to display the remainder.
 */
const DSPToggle: React.FC<IDSPToggleProps> = (props) => {
  const {
    options,
    selectedCustomer,
    setSelectedCustomer,
    variation,
    disabled,
    limit = 5,
    isIconDisplayed,
  } = props;

  const uniqueId = generateRandomHash();

  const [isExpanded, setIsExpanded] = useState(false);
  const hasMoreOptions = options.length > limit;
  const hasHiddenSelectedCustomer = options.some(isActiveAndHidden);
  const customerValues = ['all', ...DSPCustomers];
  const withIcons = (option: IToggleOption): boolean => {
    return customerValues.includes(option.value);
  };

  function isActiveAndHidden(option: IToggleOption, index: number): boolean {
    return (
      (option.value === selectedCustomer.value ||
        (option.value === 'itunes' && selectedCustomer.value === 'apple')) &&
      index + 1 > limit
    );
  }

  function handleOnChange(event: any): void {
    if (event.target.value !== selectedCustomer.value) {
      const customer = options.find(findCustomer);
      setSelectedCustomer(customer);
    }

    function findCustomer(customer: IToggleOption): boolean {
      return customer.value === event.target.value;
    }
  }

  const checked = (option: any): any =>
    option.value === selectedCustomer.value ||
    (option.value === 'itunes' && selectedCustomer.value === 'apple');

  return (
    <RootLayout>
      <LayoutWrapper limit={limit}>
        {options
          .filter((_, index) => isExpanded || index < limit)
          .map((option) => (
            <Fragment key={`${option.value}-radio-input`}>
              <Radio
                type="radio"
                name={`DSP-toggle-${variation}-${uniqueId}`}
                value={option.value}
                id={`${option.value}-${variation}-${uniqueId}`}
                checked={checked(option)}
                onChange={handleOnChange}
                disabled={disabled}
              />
              <Label htmlFor={`${option.value}-${variation}-${uniqueId}`}>
                <Media>
                  {isIconDisplayed && withIcons(option) && (
                    <DSPToggleMediaFigure>
                      <CustomerIcon aria-hidden customer={option.value} />
                    </DSPToggleMediaFigure>
                  )}
                  <DspToggleMediaBody>
                    <span className="value">{option.label}</span>
                    {option.positionsCount > 0 && (
                      <span className="positions">
                        {option.positionsCount}{' '}
                        {pluralize('chart', option.positionsCount)}
                      </span>
                    )}
                  </DspToggleMediaBody>
                </Media>
              </Label>
            </Fragment>
          ))}
      </LayoutWrapper>

      {hasMoreOptions && (
        <ShowMore
          type="button"
          onClick={(): void => setIsExpanded(!isExpanded)}
          isExpanded={isExpanded}
          showActive={hasHiddenSelectedCustomer}
        >
          +{options.length - limit} more
          <Chevron aria-hidden />
        </ShowMore>
      )}
    </RootLayout>
  );
};

export default DSPToggle;
